'use client';

import { c as _c } from "react/compiler-runtime";
import { ClerkProvider } from '@clerk/nextjs';
import { ClerkRefreshIndicator } from '@lib/auth/components/clerk-refresh-indicator';
import { ClerkSessionBridge } from '@lib/auth/components/clerk-session-bridge';
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider Wrapper
 *
 * Wraps the app with ClerkProvider to enable Clerk hooks throughout the application.
 * The provider is always present but Clerk features are only used when the
 * 'use-clerk-auth' feature flag is enabled.
 *
 * This approach allows us to use Clerk hooks (like useSignIn) conditionally without
 * errors, since they must be used within a ClerkProvider context.
 *
 * Side-effect children:
 *
 * - `<ClerkSessionBridge />`     exposes the active session to the non-React
 *                                TanStack cache handlers in
 *                                `@lib/query/query-client` so they can
 *                                refresh the Clerk token on a 401, and
 *                                drives `useClerkVisibilityRefresh`. See
 *                                `@lib/auth/clerk-session-bridge`.
 * - `<ClerkRefreshIndicator />`  subscribes to the bridge's refresh-state
 *                                pub/sub and renders a small top-center
 *                                "Reconnecting…" pill while a real refresh
 *                                is in flight (debounced) or after a
 *                                failed refresh.
 */
export function AuthProvider(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  const publishableKey = process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ClerkSessionBridge data-sentry-element="ClerkSessionBridge" data-sentry-source-file="auth-provider.tsx" />;
    t2 = <ClerkRefreshIndicator data-sentry-element="ClerkRefreshIndicator" data-sentry-source-file="auth-provider.tsx" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <ClerkProvider publishableKey={publishableKey} data-sentry-element="ClerkProvider" data-sentry-component="AuthProvider" data-sentry-source-file="auth-provider.tsx">{t1}{t2}{children}</ClerkProvider>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}